// MESERI 1985 (PE-13): P = 5X/129 + 5Y/26 + B, con B = 1 si hay brigada.
// "Distancia a bomberos" sale de km y minutos (el peor de los dos) y "carga térmica", del Qs del sector.

import { ESCALA_MESERI, FACTORES_X, FactorX, FUENTE_MESERI, MEDIOS_Y, MedioY } from './pe-constantes-meseri';
import { Traza } from './pe-tipos';
import { mjAMcal, numEc } from './pe-unidades';

const METODO = 'meseri';
export const X_MAXIMO = 129;
export const Y_MAXIMO = 26;

export function pMeseri(x: number, y: number, brigada: number): number {
  return (5 * x) / X_MAXIMO + (5 * y) / Y_MAXIMO + brigada;
}

const puntosDe = (factor: FactorX, clave: string): number => {
  const op = FACTORES_X[factor].opciones.find(o => o.clave === clave);
  if (!op) throw new Error(`Opción "${clave}" no existe en el factor ${factor}`);
  return op.puntos;
};

// Límites tal como los escribe la tabla ("menor de 5", "entre 5 y 10", …). En el borde exacto se toma
// el tramo peor (10 km → 6 puntos). Se calcula con km y con minutos y gana el peor (PE-18).
function puntosPorTramo(v: number): number {
  if (v < 5) return 10;
  if (v < 10) return 8;
  if (v < 15) return 6;
  if (v <= 25) return 2;
  return 0;
}

export function distanciaBomberosPuntos(km: number, minutos: number): number {
  if (km < 0 || minutos < 0) throw new Error('Distancia o tiempo negativos');
  return Math.min(puntosPorTramo(km), puntosPorTramo(minutos));
}

// Q en Mcal/m², del sector evaluado (PE-18d): baja < 100; media de 100 a 200; alta > 200.
export function cargaTermicaPuntos(qsMJm2: number): { puntos: number; mcalM2: number } {
  const mcalM2 = mjAMcal(qsMJm2);
  const puntos = mcalM2 < 100 ? 10 : mcalM2 <= 200 ? 5 : 0;
  return { puntos, mcalM2 };
}

export interface EntradaMeseri {
  // Opción elegida de cada factor, salvo distancia_bomberos y carga_termica, que se calculan.
  factores: Partial<Record<FactorX, string>>;
  distanciaBomberos: { km: number; minutos: number };
  qsSectorMJm2: number;
  medios: Partial<Record<MedioY, 'no' | 'sv' | 'cv'>>;
  brigada: boolean;
}

// P > 5 aceptable; P < 5 no aceptable; P = 5 exacto, "en el límite" (por validar, PE-35).
export type Aceptabilidad = 'aceptable' | 'no_aceptable' | 'en_el_limite';

export function aceptabilidadMeseri(p: number): Aceptabilidad {
  if (Math.abs(p - ESCALA_MESERI.limiteAceptable) < 1e-9) return 'en_el_limite';
  return p > ESCALA_MESERI.limiteAceptable ? 'aceptable' : 'no_aceptable';
}

export function cualitativaMeseri(p: number): string {
  const tramos = ESCALA_MESERI.cualitativa;
  return (tramos.find(t => p <= t.hasta + 1e-9) ?? tramos[tramos.length - 1]).etiqueta;
}

export interface ResultadoMeseri {
  x: number;
  y: number;
  b: number;
  p: number;
  aceptabilidad: Aceptabilidad;
  cualitativa: string;
  desgloseX: Record<FactorX, number>;
  trazas: Traza[];
}

export function calcularMeseri(e: EntradaMeseri): ResultadoMeseri {
  const desgloseX = {} as Record<FactorX, number>;
  const claves = Object.keys(FACTORES_X) as FactorX[];
  for (const f of claves) {
    if (f === 'distancia_bomberos' || f === 'carga_termica') continue;
    const clave = e.factores[f];
    if (clave === undefined) throw new Error(`Falta el factor ${f}`);
    desgloseX[f] = puntosDe(f, clave);
  }
  desgloseX.distancia_bomberos = distanciaBomberosPuntos(e.distanciaBomberos.km, e.distanciaBomberos.minutos);
  const ct = cargaTermicaPuntos(e.qsSectorMJm2);
  desgloseX.carga_termica = ct.puntos;

  const x = claves.reduce((a, f) => a + desgloseX[f], 0);
  const y = (Object.keys(e.medios) as MedioY[]).reduce((a, m) => {
    const v = e.medios[m];
    return a + (v === 'cv' ? MEDIOS_Y[m].cv : v === 'sv' ? MEDIOS_Y[m].sv : 0);
  }, 0);
  const b = e.brigada ? 1 : 0;
  const p = pMeseri(x, y, b);
  const aceptabilidad = aceptabilidadMeseri(p);
  const cualitativa = cualitativaMeseri(p);
  const etiquetaAcept = { aceptable: 'Aceptable', no_aceptable: 'No aceptable', en_el_limite: 'En el límite (P = 5)' }[aceptabilidad];

  const trazas: Traza[] = [
    {
      metodo: METODO, norma: FUENTE_MESERI.formula.norma, articulo_o_tabla: 'P = 5X/129 + 5Y/26 + B',
      umbral: null, valor: p, unidad: '—', estado: 'informativo', fuente_doc: FUENTE_MESERI.formula.fuente_doc,
      nota: `X = ${x}, Y = ${y}, B = ${b}`,
    },
    {
      metodo: METODO, norma: FUENTE_MESERI.factores.norma, articulo_o_tabla: 'Escala de P',
      umbral: `> ${ESCALA_MESERI.limiteAceptable}`, valor: p, unidad: '—',
      estado: aceptabilidad === 'en_el_limite' ? 'por_validar' : aceptabilidad === 'aceptable' ? 'cumple' : 'no_cumple',
      fuente_doc: FUENTE_MESERI.factores.fuente_doc,
      nota: `${etiquetaAcept}; riesgo ${cualitativa}${aceptabilidad === 'en_el_limite' ? '. P = 5 exacto: pendiente de decisión' : ''}`,
    },
    {
      metodo: METODO, norma: FUENTE_MESERI.factores.norma, articulo_o_tabla: 'Factor carga térmica',
      umbral: 'baja < 100; alta > 200', valor: ct.mcalM2, unidad: 'Mcal/m²', estado: 'informativo',
      fuente_doc: FUENTE_MESERI.factores.fuente_doc,
      nota: `Carga térmica del sector evaluado (${ct.puntos} puntos)`,
    },
    {
      metodo: METODO, norma: FUENTE_MESERI.factores.norma, articulo_o_tabla: 'Factor distancia de los bomberos',
      umbral: null, valor: `${numEc(e.distanciaBomberos.km)} km / ${numEc(e.distanciaBomberos.minutos)} min`, unidad: '—',
      estado: 'informativo', fuente_doc: FUENTE_MESERI.factores.fuente_doc,
      nota: `${desgloseX.distancia_bomberos} puntos (el peor de km y minutos)`,
    },
  ];
  return { x, y, b, p, aceptabilidad, cualitativa, desgloseX, trazas };
}
